import * as React from 'react';
import { Form, Formik } from 'formik';
import Box from '@mui/material/Box';
import { LoadingButton } from '@mui/lab';
import { Stack, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { toast } from 'react-toastify';
import { ValidationError } from 'yup';
import {
  GetWaiversData,
  MinorFormValues,
  minorSettingsSchema,
} from '../../../types/waivers';
import useUpdateWaiver from '../../../hooks/waivers/useUpdateWaiver';
import '../../form/form.css';
import SwitchInput from '../../form/base/SwitchInput';
import OptionsList from '../OptionsList';
import useBusiness from '../../../hooks/businesses/useBusiness';

interface Props {
  onSuccess?: () => void;
  draft: GetWaiversData | null | undefined;
}

export default function MinorForm({ onSuccess, draft }: Props) {
  const { isIndependent } = useBusiness();

  const [formError, setFormError] = React.useState('');

  const { mutation, isPending } = useUpdateWaiver({ draft });

  const [initialFormState] = React.useState<MinorFormValues>({
    requireGuardianInformation:
      draft?.minorSettings?.requireGuardianInformation ?? true,
    requireGuardianSignature:
      draft?.minorSettings?.requireGuardianSignature ?? true,
    requireRecipientSignature:
      draft?.minorSettings?.requireRecipientSignature ?? true,
    acknowledgmentOptions: (
      draft?.minorSettings?.acknowledgmentOptions || []
    ).sort((a, b) => a.order - b.order),
  });

  const onSubmit = async (values: MinorFormValues) => {
    if (!draft?.businessId || isPending) return;
    try {
      setFormError('');

      const response = await mutation.mutateAsync({
        minorSettings: {
          requireGuardianInformation: values.requireGuardianInformation,
          requireGuardianSignature: values.requireGuardianSignature,
          requireRecipientSignature: values.requireRecipientSignature,
          acknowledgmentOptions: values.acknowledgmentOptions,
        },
      });

      if (response) {
        toast.success('Minor settings saved successfully');
        onSuccess?.();
      }
    } catch (error) {
      if (error instanceof ValidationError) {
        setFormError(error.message);
      } else {
        toast.error('Failed to save minor settings');
      }
    }
  };

  const serviceSpecificInstructions = React.useMemo(() => {
    if (isIndependent) {
      return 'To update, go to the Manage Shop page under the Booking tab (for tattoos) or the Services tab (for other services).';
    }
    return 'To update, go to the Manage Shop page under the Booking tab. There, you can update the settings for tattoos and each individual service.';
  }, [isIndependent]);

  return (
    <Box>
      <Formik
        enableReinitialize
        initialValues={initialFormState}
        validationSchema={minorSettingsSchema}
        onSubmit={onSubmit}>
        {({ isSubmitting, isValid, dirty, values, setFieldValue }) => (
          <Form>
            {formError && (
              <Typography variant="body1" color="primary">
                {formError}
              </Typography>
            )}
            <Stack width="100%" spacing={2.5}>
              <SwitchInput
                name="requireGuardianInformation"
                label="Collect parent/legal guardian information"
                description="Require parent or legal guardian to provide their information when completing the waiver"
                disabled={isSubmitting}
              />
              <SwitchInput
                name="requireGuardianSignature"
                label="Collect parent/legal guardian signature"
                description="Require parent or legal guardian to sign the waiver"
                disabled={isSubmitting}
              />
              <SwitchInput
                name="requireRecipientSignature"
                label="Collect recipient signature"
                description="Require the minor recipient to sign the waiver"
                disabled={isSubmitting}
              />
              <Box
                p={2}
                bgcolor={theme => theme.palette.lightGrey.main}
                borderRadius={1}>
                <Stack>
                  <Typography variant="h5">ID Upload</Typography>
                  <Typography variant="body2">
                    This setting is service-specific.{' '}
                    {serviceSpecificInstructions}
                  </Typography>
                </Stack>
              </Box>
              <Box
                p={2}
                bgcolor={theme => theme.palette.lightGrey.main}
                borderRadius={1}>
                <Stack>
                  <Typography variant="h5">Birth Certificate Upload</Typography>
                  <Typography variant="body2">
                    This setting is service-specific.{' '}
                    {serviceSpecificInstructions}
                  </Typography>
                </Stack>
              </Box>
              <Box
                p={2}
                bgcolor={theme => theme.palette.lightGrey.main}
                borderRadius={1}>
                <Stack>
                  <Typography variant="h5">
                    Parent/Guardian ID Upload
                  </Typography>
                  <Typography variant="body2">
                    This setting is service-specific.{' '}
                    {serviceSpecificInstructions}
                  </Typography>
                </Stack>
              </Box>
              <Stack width="100%">
                <Box display="flex" gap={0.75} alignItems="center">
                  <Typography variant="h5">
                    Custom Minor Acknowledgment Options
                  </Typography>
                  <Tooltip
                    title="Dynamic variables are available for use in minor acknowledgment options. The variables [MINIMUM_AGE] and [AGE_UNIT] will be automatically populated based on the service-specific minimum age settings configured for the appointment type (tattoo or other services)."
                    arrow
                    placement="top"
                    enterTouchDelay={0}>
                    <InfoOutlinedIcon
                      sx={{ fontSize: 12, cursor: 'pointer' }}
                    />
                  </Tooltip>
                </Box>
                <OptionsList
                  options={values.acknowledgmentOptions}
                  onChange={newOptions =>
                    setFieldValue('acknowledgmentOptions', newOptions)
                  }
                  placeholder="Type question"
                  addText="Add new question"
                  askForType
                  disabled={isSubmitting}
                  combineAllOptions
                  allowedTypes={['CHECKBOX', 'MULTILINE_TEXT']}
                />
              </Stack>
            </Stack>

            <LoadingButton
              type="submit"
              disabled={!isValid || isSubmitting || !dirty || isPending}
              variant="contained"
              className="primary"
              loading={isSubmitting}>
              <span>Save</span>
            </LoadingButton>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
